<template>
  <div v-if="approvalState.visible && approvalState.request" class="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4">
    <div class="w-full max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Supervisor Approval Required</p>
            <h2 class="mt-1 text-lg font-bold text-slate-900 dark:text-white">{{ approvalTitle }}</h2>
          </div>
          <span class="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider" :class="riskBadgeClass">
            {{ riskLabel }}
          </span>
        </div>
      </div>

      <div class="px-6 py-5 space-y-5">
        <p class="text-sm text-slate-600 dark:text-slate-300">
          {{ approvalDescription }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
            <p class="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Requester</p>
            <p class="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{{ requesterLabel }}</p>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400 break-all">{{ requesterOrigin }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
            <p class="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Action</p>
            <p class="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{{ approvalState.request.action }}</p>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">{{ approvalState.request.kind }} via {{ approvalState.request.transport }}</p>
          </div>
        </div>

        <div class="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4 space-y-3">
          <div class="flex items-center justify-between gap-4">
            <p class="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Payload Preview</p>
            <p class="text-[11px] text-slate-400 dark:text-slate-500">Request ID: {{ approvalState.request.requestId }}</p>
          </div>
          <pre class="max-h-72 overflow-auto rounded-lg bg-slate-950 text-slate-100 text-xs p-4 whitespace-pre-wrap break-all">{{ payloadPreview }}</pre>
        </div>
      </div>

      <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          @click="denyRequest"
        >
          Deny
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lg bg-rad-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
          @click="approveRequest"
        >
          Approve
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { resolveSupervisorApproval, useSupervisorApprovalState } from '@/supervisor/approval-store'

const approvalState = useSupervisorApprovalState()

const approvalTitle = computed(() => approvalState.definition?.title || approvalState.request?.action || 'Supervisor approval')
const approvalDescription = computed(() => approvalState.definition?.description || 'A channel or agent is requesting permission to perform an action through RADSuite.')
const riskLabel = computed(() => approvalState.definition?.riskLevel || approvalState.request?.metadata?.riskHint || 'medium')
const requesterLabel = computed(() => approvalState.request?.metadata?.requesterName || approvalState.request?.requesterType || 'Unknown requester')
const requesterOrigin = computed(() => approvalState.request?.metadata?.origin || approvalState.request?.channelId || approvalState.request?.agentId || 'Unknown origin')
const payloadPreview = computed(() => JSON.stringify(approvalState.request?.payload ?? {}, null, 2))

const riskBadgeClass = computed(() => {
  if (riskLabel.value === 'high') {
    return 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
  }

  if (riskLabel.value === 'low') {
    return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
  }

  return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
})

function approveRequest() {
  if (!approvalState.request) return

  resolveSupervisorApproval({
    requestId: approvalState.request.requestId,
    ok: true,
    status: 'allowed'
  })
}

function denyRequest() {
  if (!approvalState.request) return

  resolveSupervisorApproval({
    requestId: approvalState.request.requestId,
    ok: false,
    status: 'denied',
    error: {
      code: 'USER_DENIED',
      message: 'The supervisor denied this request.'
    }
  })
}
</script>
